import { Link } from "react-router-dom";
import { Container } from "lucide-react";
import useWebsiteSettings from "@/hooks/useWebsiteSettings";

/**
 * Site logo.
 *
 * Uses the logo uploaded in /admin/settings when one is set, otherwise falls
 * back to the built-in icon + wordmark. The fallback is not a placeholder — it
 * is the intended mark until the operator supplies artwork, and it still renders
 * if the upload 404s.
 *
 * The image is height-constrained with `object-contain` and `w-auto`: a logo is
 * a fixed piece of artwork, so it must never be cropped or stretched, whatever
 * proportions the uploaded file has.
 */
export default function Logo({ variant = "light", size = "md", withTag = true }) {
    const settings = useWebsiteSettings();
    const isDark = variant === "dark";
    const wordmarkColor = isDark ? "text-white" : "text-cs-navy";
    const accent = "text-cs-orange";
    const tagColor = isDark ? "text-white/60" : "text-cs-steel";
    const dimensions = size === "sm" ? "w-7 h-7" : "w-9 h-9";
    const logoUrl = settings?.logoUrl;

    return (
        <Link
            to="/"
            className="inline-flex items-center gap-3 group"
            aria-label="Container Specialist — Home"
            data-testid="site-logo"
        >
            {logoUrl ? (
                // The header and footer are navy, and an uploaded image cannot
                // recolour itself the way the wordmark below does — a logo drawn
                // in dark ink would simply vanish. Sitting it on a light plate
                // keeps any artwork legible, whatever colours it uses.
                <span
                    className={
                        isDark
                            ? "inline-flex items-center rounded-md bg-white px-2.5 py-1.5"
                            : "inline-flex items-center"
                    }
                >
                    <img
                        src={logoUrl}
                        alt="Container Specialist"
                        className={`${size === "sm" ? "h-7" : "h-9 md:h-10"} w-auto max-w-[200px] object-contain`}
                        data-testid="site-logo-image"
                    />
                </span>
            ) : (
                <>
                    <span
                        className={`${dimensions} inline-flex items-center justify-center border ${
                            isDark ? "border-white/40" : "border-cs-navy"
                        } bg-transparent`}
                    >
                        <Container
                            className={`${accent}`}
                            strokeWidth={2}
                            size={size === "sm" ? 16 : 20}
                        />
                    </span>
                    <span className="flex flex-col leading-none">
                        <span
                            className={`font-heading font-extrabold tracking-tight ${wordmarkColor} text-lg md:text-xl uppercase`}
                        >
                            Container<span className={`${accent}`}>·</span>Specialist
                        </span>
                        {withTag && (
                            <span
                                className={`font-mono ${tagColor} text-[9px] uppercase tracking-[0.28em] mt-0.5`}
                            >
                                MK Holdings · Since 2010
                            </span>
                        )}
                    </span>
                </>
            )}
        </Link>
    );
}
